/**
 * Contrato das residências e das pessoas da família.
 *
 * Papel: aqui ficam os schemas dos dois cadastros que os demais módulos
 * referenciam por chave estrangeira — `novaResidenciaSchema` e
 * `novaPessoaFamiliaSchema` na entrada, `residenciaSchema` e
 * `pessoaFamiliaSchema` na saída.
 *
 * Como usar: a residência trafega decomposta em `nome`, `cidade` e `uf`, e
 * nunca como `Fazenda Boa Vista (Porto Feliz, SP)`. Quem precisar da forma
 * longa a monta na renderização, com `formatarResidencia`; mandar a forma
 * composta no campo `nome` grava a cidade dentro dele e faz o filtro por estado
 * deixar a linha de fora.
 *
 * Os dois cadastros são pequenos e quase estáticos, e por isso as listagens
 * devolvem tudo de uma vez — não há `parametrosListagemSchema` nem envelope
 * paginado aqui, e um consumidor que espere `itens` e `total` não encontra
 * nenhum dos dois.
 */
import { z } from 'zod';

import { idSchema, textoObrigatorio } from './comum';
import { ufSchema } from './formatos';

/**
 * O que o formulário envia para criar ou alterar uma residência.
 *
 * `nome` é a forma curta, que identifica o lugar, e é único no banco: quem
 * cadastrar a mesma residência outra vez recebe 409, e não uma segunda linha
 * para a qual metade dos pedidos passaria a apontar.
 *
 * `apelido` é o nome curto que cabe onde `nome` não cabe — coluna estreita,
 * chip de filtro, cabeçalho de seção. É obrigatório porque a tela que o mostra
 * não tem um segundo lugar para onde recorrer quando ele falta, e acabaria
 * exibindo espaço em branco no lugar do nome da casa.
 *
 * `cidade` é texto livre e `uf` é escolha entre as vinte e sete siglas. A
 * assimetria é proposital: a sigla do estado não muda, o nome da cidade muda
 * por lei municipal.
 *
 * `dentroDeId` é o lugar que contém este, e é nulo em quem não está dentro de
 * nada.
 *
 * A regra de que uma residência não pode ficar dentro de si mesma **não** está
 * aqui: este schema descreve o corpo da requisição, e o corpo não traz o
 * identificador da linha que está sendo alterada. Quem compara os dois é
 * `functions/cadastros-base.ts`, que recebe o identificador pela rota e responde
 * 409. Um `refine` neste schema teria forma de regra sem poder de recusar nada.
 */
export const novaResidenciaSchema = z.object({
  nome: textoObrigatorio('Nome'),
  apelido: textoObrigatorio('Apelido', 40),
  cidade: textoObrigatorio('Cidade', 120),
  uf: ufSchema,
  dentroDeId: idSchema.nullish().transform((valor) => valor ?? null),
});

export type NovaResidencia = z.infer<typeof novaResidenciaSchema>;

/** O lugar que contém a residência, como a listagem o traz aninhado. */
export const continenteSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/**
 * Uma residência como a API a devolve.
 *
 * `dentroDe` vem aninhado, e não apenas como identificador, porque a tabela
 * mostra o nome do lugar que contém em toda linha — sem ele, a tela faria uma
 * segunda passagem sobre a própria listagem para traduzir os identificadores.
 */
export const residenciaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
  cidade: z.string(),
  uf: z.string(),
  dentroDe: continenteSchema.nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Residencia = z.infer<typeof residenciaSchema>;

/**
 * O que o formulário envia para criar ou alterar uma pessoa da família.
 *
 * `nomeCompleto` é o nome inteiro, e é ele que identifica: dois membros da
 * família compartilham o primeiro nome, e um cadastro por primeiro nome
 * juntaria duas pessoas — e os documentos de ambas — num registro só.
 *
 * `apelido` é digitado, não derivado. Não se obtém do nome removendo acentos
 * nem cortando o primeiro nome, e qualquer regra que tentasse deduzi-lo
 * acertaria em parte da família e erraria no resto.
 */
export const novaPessoaFamiliaSchema = z.object({
  nomeCompleto: textoObrigatorio('Nome completo'),
  apelido: textoObrigatorio('Apelido', 40),
});

export type NovaPessoaFamilia = z.infer<typeof novaPessoaFamiliaSchema>;

/** Uma pessoa da família como a API a devolve. */
export const pessoaFamiliaSchema = novaPessoaFamiliaSchema.extend({
  id: idSchema,
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type PessoaFamilia = z.infer<typeof pessoaFamiliaSchema>;
